import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import StatutBadge from './StatutBadge';
import { CATEGORIES_PIECE, statutLabel, personName, fmtDate, fmtDateTime } from './constants';

const QUICK = {
  nouvelle:  [{ label: '✅ Valider', statut: 'a_traiter' }, { label: '🚫 Refuser', statut: 'refusee', danger: true }],
  a_traiter: [{ label: '▶️ Démarrer', statut: 'en_cours' }],
  planifiee: [{ label: '▶️ Démarrer', statut: 'en_cours' }],
  en_cours:  [{ label: '🏁 Terminer', statut: 'terminee' }],
  terminee:  [{ label: '↩️ Rouvrir', statut: 'en_cours' }],
  refusee:   [{ label: '↩️ Rouvrir', statut: 'a_traiter' }],
  annulee:   [{ label: '↩️ Rouvrir', statut: 'a_traiter' }],
};

const EVENT_ICON = {
  creation: '🆕', statut: '🔄', affectation: '👷', planification: '📅', commentaire: '💬', cloture: '🏁', piece: '📎',
};

function eventText(e) {
  switch (e.type) {
    case 'creation': return 'a créé la demande';
    case 'statut': return `a changé le statut : ${statutLabel(e.ancien_statut)} → ${statutLabel(e.nouveau_statut)}`;
    case 'cloture': return 'a clôturé la demande (terminée)';
    case 'affectation': return "a mis à jour l'affectation";
    case 'planification': return "a planifié l'intervention";
    case 'piece': return e.commentaire || 'a ajouté une pièce jointe';
    default: return e.commentaire || '';
  }
}

export default function DemandeDetail({ currentUser, refs, demandeId, onBack, onEdit }) {
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super-admin';
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [pieceUrls, setPieceUrls] = useState({});
  const [cat, setCat] = useState('Photo');
  const [aff, setAff] = useState({ prestataire_id: '', responsable_id: '', date_planifiee: '', passer_planifiee: false });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/maintenance/demandes/${demandeId}`);
      setD(data);
      setAff({
        prestataire_id: data.prestataire_id || '',
        responsable_id: data.responsable_id || '',
        date_planifiee: data.date_planifiee ? data.date_planifiee.slice(0, 10) : '',
        passer_planifiee: false,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Demande introuvable ou accès refusé.');
    } finally {
      setLoading(false);
    }
  }, [demandeId]);
  useEffect(() => { load(); }, [load]);

  // Récupère les pièces en blob (auth) pour affichage des miniatures
  useEffect(() => {
    if (!d?.pieces?.length) return;
    let revoked = [];
    (async () => {
      const map = {};
      for (const p of d.pieces) {
        try {
          const res = await api.get(`/maintenance/pieces/${p.id}/download`, { responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          map[p.id] = url; revoked.push(url);
        } catch { /* ignore */ }
      }
      setPieceUrls(map);
    })();
    return () => { revoked.forEach((u) => URL.revokeObjectURL(u)); };
  }, [d?.pieces]);

  const canUpload = isAdmin || d?.demandeur_id === currentUser.id;

  const changeStatut = async (statut) => {
    setBusy(true);
    try { await api.patch(`/maintenance/demandes/${demandeId}/statut`, { statut }); await load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  const saveAffectation = async () => {
    setBusy(true);
    try {
      await api.patch(`/maintenance/demandes/${demandeId}/affectation`, {
        prestataire_id: aff.prestataire_id || null,
        responsable_id: aff.responsable_id || null,
        date_planifiee: aff.date_planifiee || null,
        passer_planifiee: aff.passer_planifiee,
      });
      await load();
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try { await api.post(`/maintenance/demandes/${demandeId}/commentaires`, { commentaire: comment }); setComment(''); await load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('fichier', file);
    fd.append('categorie', cat);
    setBusy(true);
    try { await api.post(`/maintenance/demandes/${demandeId}/pieces`, fd); await load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
    finally { setBusy(false); }
  };

  const deletePiece = async (id) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    try { await api.delete(`/maintenance/pieces/${id}`); await load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const removeDemande = async () => {
    if (!window.confirm(`Supprimer définitivement la demande ${d.numero} ?\n\nCette action est irréversible : l'historique et les pièces jointes seront aussi supprimés. Pour conserver la trace, préférez « Annuler la demande ».`)) return;
    setBusy(true);
    try { await api.delete(`/maintenance/demandes/${demandeId}`); onBack(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); setBusy(false); }
  };

  const canDelete = currentUser.role === 'super-admin';

  if (loading) return <div className="table-empty">Chargement…</div>;
  if (error) return (
    <div>
      <button className="mnt-back" onClick={onBack}>← Liste des demandes</button>
      <div className="table-container" style={{ marginTop: 12 }}><div className="table-empty">{error}</div></div>
    </div>
  );

  const quick = QUICK[d.statut] || [];
  const nonTerminal = !['terminee', 'refusee', 'annulee'].includes(d.statut);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
      <div className="page-header-row">
        <button className="mnt-back" onClick={onBack}>← Liste des demandes</button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-secondary" onClick={() => onEdit(demandeId)}>✏️ Modifier</button>
          {canDelete && <button className="btn-danger" disabled={busy} onClick={removeDemande}>🗑️ Supprimer</button>}
        </div>
      </div>

      <div className="mnt-detail-head">
        <div className="mnt-detail-title">
          <span className="mnt-detail-num">{d.numero}</span>
          <h2>{d.titre}</h2>
          <StatutBadge statut={d.statut} />
          {d.urgence && <span className="mnt-chip mnt-chip-urgence">URGENT</span>}
          {d.astreinte && <span className="mnt-chip mnt-chip-astreinte">ASTREINTE</span>}
        </div>
        <div className="mnt-meta-line">
          <span>📍 <strong>{d.site_nom || '—'}</strong>{d.localisation_precise ? ` · ${d.localisation_precise}` : ''}</span>
          <span>🔧 {d.activite_nom || '—'}</span>
          <span>👤 {personName(d, 'demandeur_')}</span>
          <span>🗓️ Créée le {fmtDate(d.created_at)}</span>
          {d.date_limite && <span>⏳ Limite : {fmtDate(d.date_limite)}</span>}
        </div>
      </div>

      <div className="mnt-detail-grid">
        {/* ─── Colonne principale ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
          <div className="mnt-card">
            <h3>Description</h3>
            {d.description ? <div className="mnt-prose">{d.description}</div> : <div className="mnt-muted">Aucune description.</div>}
            {isAdmin && d.note_interne && (
              <>
                <h3 style={{ marginTop: 16 }}>Note interne</h3>
                <div className="mnt-prose">{d.note_interne}</div>
              </>
            )}
          </div>

          <div className="mnt-card">
            <h3>Pièces jointes</h3>
            {d.pieces.length === 0 && <div className="mnt-muted" style={{ marginBottom: 10 }}>Aucune pièce jointe.</div>}
            <div className="mnt-gallery">
              {d.pieces.map((p) => {
                const isImg = (p.mime || '').startsWith('image/');
                const url = pieceUrls[p.id];
                return (
                  <div key={p.id} className="mnt-thumb-wrap">
                    <div className="mnt-thumb" title={`${p.nom_fichier} (${p.categorie})`} onClick={() => url && window.open(url, '_blank')}>
                      {isImg && url ? <img src={url} alt={p.nom_fichier} /> : <span>📄<br />{p.categorie}</span>}
                    </div>
                    {canUpload && <button className="mnt-thumb-del" title="Supprimer" onClick={() => deletePiece(p.id)}>✕</button>}
                  </div>
                );
              })}
            </div>
            {canUpload && (
              <div className="mnt-row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0, flex: '0 0 130px' }}>
                  <label>Catégorie</label>
                  <select className="form-control" value={cat} onChange={(e) => setCat(e.target.value)}>
                    {CATEGORIES_PIECE.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <button type="button" className="btn-secondary" disabled={busy} onClick={() => fileRef.current?.click()}>📎 Ajouter un fichier</button>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="mnt-hidden-input"
                  onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); e.target.value = ''; }} />
              </div>
            )}
          </div>

          <div className="mnt-card">
            <h3>Suivi</h3>
            <div className="mnt-timeline">
              {d.historique.map((e) => (
                <div key={e.id} className="mnt-event">
                  <span className="mnt-event-icon">{EVENT_ICON[e.type] || '•'}</span>
                  <div className="mnt-event-body">
                    <div className="mnt-event-text"><strong>{personName(e)}</strong> {eventText(e)}</div>
                    <div className="mnt-event-meta">{fmtDateTime(e.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mnt-comment-box">
              <textarea className="form-control mnt-textarea" placeholder="Ajouter un commentaire…" value={comment} onChange={(e) => setComment(e.target.value)} />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn-primary" disabled={busy || !comment.trim()} onClick={addComment}>Commenter</button>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Colonne latérale : traitement ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
          {isAdmin ? (
            <>
              <div className="mnt-card">
                <h3>Traitement</h3>
                <div className="mnt-quick-actions">
                  {quick.map((a) => (
                    <button key={a.statut} className={a.danger ? 'btn-danger' : 'btn-primary'} disabled={busy} onClick={() => changeStatut(a.statut)}>{a.label}</button>
                  ))}
                  {nonTerminal && <button className="btn-secondary" disabled={busy} onClick={() => changeStatut('annulee')}>Annuler la demande</button>}
                </div>
              </div>

              <div className="mnt-card">
                <h3>Affectation</h3>
                <div className="form-group">
                  <label>Prestataire</label>
                  <select className="form-control" value={aff.prestataire_id} onChange={(e) => setAff({ ...aff, prestataire_id: e.target.value })}>
                    <option value="">— Aucun —</option>
                    {refs.prestataires.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Responsable maintenance</label>
                  <select className="form-control" value={aff.responsable_id} onChange={(e) => setAff({ ...aff, responsable_id: e.target.value })}>
                    <option value="">— Aucun —</option>
                    {refs.users.map((u) => <option key={u.id} value={u.id}>{personName(u)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date d'intervention planifiée</label>
                  <input type="date" className="form-control" value={aff.date_planifiee} onChange={(e) => setAff({ ...aff, date_planifiee: e.target.value })} />
                </div>
                {['nouvelle', 'a_traiter'].includes(d.statut) && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12 }}>
                    <input type="checkbox" checked={aff.passer_planifiee} onChange={(e) => setAff({ ...aff, passer_planifiee: e.target.checked })} />
                    Passer la demande en « Planifiée »
                  </label>
                )}
                <button className="btn-primary" disabled={busy} onClick={saveAffectation} style={{ width: '100%' }}>Enregistrer l'affectation</button>
              </div>
            </>
          ) : (
            <div className="mnt-card">
              <h3>Suivi</h3>
              <div className="mnt-muted">Le traitement de la demande est géré par le responsable maintenance. Vous serez informé de l'avancement via le fil de suivi.</div>
            </div>
          )}

          {d.prestataire_id && (
            <div className="mnt-card">
              <h3>Prestataire affecté</h3>
              <div className="mnt-prose" style={{ fontSize: 13 }}>
                <strong>{d.prestataire_nom}</strong>
                {d.prestataire_contact && <div className="mnt-muted">{d.prestataire_contact}</div>}
                {d.prestataire_telephone && <div className="mnt-muted">📞 {d.prestataire_telephone}</div>}
                {d.prestataire_email && <div className="mnt-muted">✉️ {d.prestataire_email}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
